import { useState, useEffect, useCallback } from "react";
import { PackageCheck, Download, Copy, CheckCircle2, AlertCircle, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { BlockCard } from "../../../components/BlockCard";
import { API, getAuthHeaders } from "./helpers";

// V1-DEP (T-242b) — « Préparer le dépôt » : un clic produit le paquet (pièces nommées
// par code Anah + fiche de dépôt) ; « Déposé » passe par la primitive de statut du
// serveur (POST /marquer-depose → set_dossier_statut). Les montants de la fiche sont
// LUS dans le plan de financement par le serveur : ce composant n'en calcule aucun.

// T-371 (S-2) — le même panneau sert les trois phases : dépôt (défaut, inchangé), demande d'avance, demande de solde.
export const TEXTES_PHASE = {
  depot: { titre: "Préparer le dépôt", bouton: "Préparer le dépôt", nonPret: "le dépôt ne peut être préparé que lorsque toutes les pièces requises sont présentes", fichier: "depot", testId: "preparer-depot-panel", ok: "Paquet de dépôt téléchargé", copie: "Fiche de dépôt copiée", voir: "Voir la fiche de dépôt" },
  avance: { titre: "Préparer la demande d'avance", bouton: "Préparer la demande d'avance", nonPret: "la demande d'avance ne peut être préparée que lorsque toutes les pièces requises sont présentes", fichier: "avance", testId: "preparer-avance-panel", ok: "Paquet de la demande d'avance téléchargé", copie: "Fiche de la demande copiée", voir: "Voir la fiche de la demande" },
  solde: { titre: "Préparer la demande de solde", bouton: "Préparer la demande de solde", nonPret: "la demande de solde ne peut être préparée que lorsque toutes les pièces requises sont présentes", fichier: "solde", testId: "preparer-solde-panel", ok: "Paquet de la demande de solde téléchargé", copie: "Fiche de la demande copiée", voir: "Voir la fiche de la demande" },
};

export function etatPanneau({ chargement, erreur, apercu }) {
  if (chargement) return "chargement";
  if (erreur) return "erreur";
  if (!apercu) return "vide";
  return apercu.pret ? "pret" : "non_pret";
}

const euros = (v) =>
  typeof v === "number" ? v.toLocaleString("fr-FR", { style: "currency", currency: "EUR" }) : "à compléter";

function Encart({ famille, children, testId }) {
  return (
    <div
      role="status"
      data-testid={testId}
      className="text-sm rounded-lg p-3 flex items-start gap-2"
      style={{ background: `var(--oh-stat-${famille}-bg)`, color: `var(--oh-stat-${famille}-text)` }}
    >
      {children}
    </div>
  );
}

export function PreparerDepotPanel({ dossierId, dossierRef, tenantId, isDepose, onUpdate, phase = "depot", dateDemande = null, annulable = true }) {
  const T = TEXTES_PHASE[phase] || TEXTES_PHASE.depot;
  const estDepot = phase === "depot" || !TEXTES_PHASE[phase];
  const suffixe = estDepot ? "" : `&phase=${phase}`;
  const [apercu, setApercu] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [preparation, setPreparation] = useState(false);
  const [marquage, setMarquage] = useState(false);

  const charger = useCallback(async () => {
    if (!dossierRef) return;
    setChargement(true);
    setErreur(null);
    try {
      const res = await fetch(
        `${API}/api/metier/agents/depot/paquet/apercu?reference=${encodeURIComponent(dossierRef)}${suffixe}`,
        { headers: getAuthHeaders(tenantId), credentials: "include" }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setErreur(err.detail || `Erreur ${res.status}`);
        setApercu(null);
      } else {
        setApercu(await res.json());
      }
    } catch (e) {
      setErreur("Erreur réseau");
    } finally {
      setChargement(false);
    }
  }, [dossierRef, tenantId, suffixe]);

  useEffect(() => { charger(); }, [charger]);

  const preparer = async () => {
    if (preparation) return;
    setPreparation(true);
    try {
      const q = `reference=${encodeURIComponent(dossierRef)}${suffixe}`;
      const res = await fetch(`${API}/api/metier/agents/depot/paquet/zip?${q}`, {
        headers: getAuthHeaders(tenantId), credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${T.fichier}_${dossierRef}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(T.ok);
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setPreparation(false);
    }
  };

  const copierFiche = async () => {
    try {
      await navigator.clipboard.writeText(apercu?.fiche || "");
      toast.success(T.copie);
    } catch (e) {
      toast.error("Copie impossible");
    }
  };

  // Générateur EXISTANT du Cerfa 13934 (demande d'avance) : même route que « Préparer & Signer » ; rien d'envoyé, le document se retrouve dans ce dossier.
  const [generation, setGeneration] = useState(false);
  const genererCerfa = async () => {
    if (generation) return;
    setGeneration(true);
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/demande-avance-anah/generer`, { method: "POST", headers: getAuthHeaders(tenantId), credentials: "include" });
      if (res.ok) toast.success("Cerfa 13934 généré : il est dans les documents à signer du dossier");
      else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail?.message || err?.detail || "Erreur génération du Cerfa 13934");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setGeneration(false);
    }
  };

  // T-372 (S-3) — « Demande d'avance faite » / « Demande de solde faite » (et annulation) : la trace d'un geste fait par une PERSONNE sur le portail ;
  // rien n'est envoyé. Le statut et la date sont posés par le serveur (primitive de statut), l'écran ne calcule rien.
  const [demandeEnCours, setDemandeEnCours] = useState(false);
  const basculerDemande = async (annuler) => {
    if (demandeEnCours) return;
    setDemandeEnCours(true);
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/${annuler ? "annuler" : "marquer"}-demande-${phase}`, {
        method: "POST", headers: getAuthHeaders(tenantId, "application/json"), credentials: "include", body: JSON.stringify({}),
      });
      if (res.ok) {
        toast.success(annuler ? "Demande annulée" : phase === "avance" ? "Demande d'avance enregistrée" : "Demande de solde enregistrée");
        onUpdate?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Action impossible");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setDemandeEnCours(false);
    }
  };
  const dateFr = dateDemande ? String(dateDemande).slice(0, 10).split("-").reverse().join("/") : null;

  const marquerDepose = async () => {
    if (marquage) return;
    setMarquage(true);
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/marquer-depose`, {
        method: "POST",
        headers: getAuthHeaders(tenantId, "application/json"),
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (res.ok) {
        toast.success("Dossier marqué comme déposé");
        onUpdate?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Impossible de marquer le dépôt");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setMarquage(false);
    }
  };

  const etat = etatPanneau({ chargement, erreur, apercu });
  const montants = apercu?.montants || {};

  return (
    <BlockCard
      icon={PackageCheck}
      iconColor="var(--oh-accent-text)"
      title={T.titre}
      testId={T.testId}
    >
      {etat === "chargement" && (
        <div className="space-y-2" data-testid="preparer-depot-chargement" aria-busy="true">
          <div className="h-4 rounded animate-pulse" style={{ background: "var(--oh-bg-surface)", width: "60%" }} />
          <div className="h-4 rounded animate-pulse" style={{ background: "var(--oh-bg-surface)", width: "85%" }} />
        </div>
      )}

      {etat === "erreur" && (
        <Encart famille="red" testId="preparer-depot-erreur">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {erreur}{" "}
            <button type="button" className="underline" onClick={charger} data-testid="preparer-depot-reessayer">
              Réessayer
            </button>
          </span>
        </Encart>
      )}

      {etat === "non_pret" && (
        <div className="space-y-3" data-testid="preparer-depot-non-pret">
          <Encart famille="amber">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              {estDepot
                ? <>Checklist {apercu.verdict || "inconnue"} : {T.nonPret}.</>
                : (apercu.conditions || []).some((c) => c.bloquante && c.ok === false)
                  ? "Cette demande n'est pas possible pour l'instant."
                  : apercu.verdict === "NON CONFIGURÉE"
                    ? "La liste des pièces de cette demande n'est pas encore validée."
                    : "Il manque des pièces pour préparer cette demande."}
            </span>
          </Encart>
          {!estDepot && (apercu.conditions || []).filter((c) => c.bloquante && c.ok === false).length > 0 && (
            <ul className="text-sm space-y-1" data-testid="preparer-phase-conditions" style={{ color: "var(--oh-text-secondary)" }}>
              {apercu.conditions.filter((c) => c.bloquante && c.ok === false).map((c) => (
                <li key={c.code}>À respecter : <span style={{ color: "var(--oh-text)" }}>{c.nom}</span></li>
              ))}
            </ul>
          )}
          {(apercu.manquantes || []).length > 0 && (
            <ul className="text-sm space-y-1" style={{ color: "var(--oh-text-secondary)" }}>
              {apercu.manquantes.map((p) => (
                <li key={p.id || p.nom}>
                  <span className="font-medium" style={{ color: "var(--oh-text)" }}>{p.id}</span> · {p.nom}
                </li>
              ))}
            </ul>
          )}
          <Button
            disabled
            title="Disponible quand la checklist est prête"
            style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}
            data-testid="preparer-depot-bouton"
            aria-label={`${T.bouton} (indisponible : checklist non prête)`}
          >
            {T.bouton}
          </Button>
          {/* T-372c : « Demande faite » est la TRACE d'un geste fait par une personne sur le portail, pas une conséquence de la vérification des pièces :
              si une pièce n'est pas visible dans OmniHub (papier, Zoho indisponible), la demande réellement faite doit pouvoir être enregistrée. Sauf si
              une condition de recevabilité est fausse (ex. mandataire financier) : la demande n'est alors pas possible, il n'y a rien à enregistrer. */}
          {!estDepot && !dateDemande && !(apercu.conditions || []).some((c) => c.bloquante && c.ok === false) && (
            <div className="space-y-2" data-testid="preparer-phase-faite-sans-pret">
              <Encart famille="amber" testId="preparer-phase-faite-avertissement">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  {apercu.verdict === "NON CONFIGURÉE" ? "La liste des pièces de cette demande n'est pas encore validée." : "OmniHub ne voit pas toutes les pièces."}
                  {" "}Si vous avez déjà fait cette demande sur le portail, vous pouvez l'enregistrer ; sinon n'y touchez pas.
                </span>
              </Encart>
              <Button
                variant="outline"
                onClick={() => basculerDemande(false)}
                disabled={demandeEnCours}
                style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" }}
                data-testid="preparer-phase-faite"
              >
                {demandeEnCours && <Loader2 className="w-4 h-4 animate-spin" />}
                {phase === "avance" ? "Demande d'avance faite" : "Demande de solde faite"}
              </Button>
            </div>
          )}
        </div>
      )}

      {etat === "pret" && (
        <div className="space-y-3" data-testid="preparer-depot-pret">
          <Encart famille="green">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Checklist prête : {apercu.nb_pieces_zip} pièce(s) dans le paquet
              {apercu.a_joindre?.length ? `, ${apercu.a_joindre.length} à joindre à la main` : ""}.
            </span>
          </Encart>

          {phase === "avance" && typeof apercu.plafond_avance === "number" && (
            <p className="text-sm" data-testid="preparer-avance-plafond" style={{ color: "var(--oh-text)" }}>
              Avance de {euros(apercu.plafond_avance)} au plus (30 % de l'aide ANAH du plan de financement).
            </p>
          )}
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm" data-testid="preparer-depot-montants">
            {[
              ["Total TTC", montants.total_ttc],
              ["Aide Anah", montants.aide_anah],
              ...(montants.depenses || []),
              ...(montants.ressources || []),
            ]
              .filter(([, valeur]) => valeur !== 0)
              .map(([libelle, valeur]) => (
              <div key={libelle} className="p-2 rounded-md" style={{ background: "var(--oh-bg-surface)" }}>
                <dt className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{libelle}</dt>
                <dd className="font-medium" style={{ color: "var(--oh-text)" }}>{euros(valeur)}</dd>
              </div>
            ))}
          </dl>
          {montants.incoherence && (
            <Encart famille="amber" testId="preparer-depot-incoherence">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{montants.incoherence}</span>
            </Encart>
          )}

          {apercu.a_joindre?.length > 0 && (
            <ul className="text-sm space-y-1" data-testid="preparer-depot-a-joindre" style={{ color: "var(--oh-text-secondary)" }}>
              {apercu.a_joindre.map((p) => (
                <li key={p.id || p.nom}>
                  <FileText className="w-3 h-3 inline mr-1" aria-hidden="true" />
                  {p.id} · {p.nom} — {p.motif}
                </li>
              ))}
            </ul>
          )}

          <details>
            <summary className="text-sm cursor-pointer" style={{ color: "var(--oh-accent-text)" }}>
              {T.voir}
            </summary>
            <pre
              className="mt-2 p-3 rounded-lg text-xs whitespace-pre-wrap overflow-x-auto"
              style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text)", border: "0.5px solid var(--oh-border)" }}
              data-testid="preparer-depot-fiche"
            >
              {apercu.fiche}
            </pre>
          </details>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={preparer}
              disabled={preparation}
              style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}
              data-testid="preparer-depot-bouton"
            >
              {preparation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {T.bouton}
            </Button>
            <Button
              variant="outline"
              onClick={copierFiche}
              style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}
              data-testid="preparer-depot-copier"
            >
              <Copy className="w-4 h-4" /> Copier la fiche
            </Button>
            {phase === "avance" && (          // le Cerfa 13934*05 est la demande d'AVANCE seulement : le solde se demande sur le portail (S-0)
              <Button
                variant="outline"
                onClick={genererCerfa}
                disabled={generation}
                style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}
                data-testid="preparer-phase-cerfa"
              >
                {generation ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Générer le Cerfa 13934
              </Button>
            )}
            {!estDepot && !dateDemande && (
              <Button
                variant="outline"
                onClick={() => basculerDemande(false)}
                disabled={demandeEnCours}
                style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" }}
                data-testid="preparer-phase-faite"
              >
                {demandeEnCours && <Loader2 className="w-4 h-4 animate-spin" />}
                {phase === "avance" ? "Demande d'avance faite" : "Demande de solde faite"}
              </Button>
            )}
            {estDepot && !isDepose && (
              <Button
                variant="outline"
                onClick={marquerDepose}
                disabled={marquage}
                style={{ borderColor: "var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" }}
                data-testid="preparer-depot-depose"
              >
                {marquage && <Loader2 className="w-4 h-4 animate-spin" />}
                Déposé
              </Button>
            )}
          </div>
        </div>
      )}

      {!estDepot && dateDemande && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Encart famille="blue" testId="preparer-phase-faite-etat">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{phase === "avance" ? "Demande d'avance faite" : "Demande de solde faite"} le {dateFr}.</span>
          </Encart>
          {annulable && <Button
            variant="outline" onClick={() => basculerDemande(true)} disabled={demandeEnCours}
            style={{ borderColor: "var(--oh-border)", color: "var(--oh-text-secondary)", background: "transparent" }}
            data-testid="preparer-phase-annuler"
          >
            Annuler la demande
          </Button>}
        </div>
      )}

      {estDepot && isDepose && etat !== "chargement" && (
        <div className="mt-3">
          <Encart famille="blue" testId="preparer-depot-depose-etat">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>Dossier déposé : la date de dépôt est enregistrée.</span>
          </Encart>
        </div>
      )}
    </BlockCard>
  );
}
